import type { ChannelEnvelope, InvitePresentation } from "@guandan/protocol";
import { createStore, type Store } from "./store.js";
import type { Activity, SocialPlayer } from "./api.js";
import type { ConnectionState } from "./PlayerChannel.js";

/**
 * One normalised view of everything the channel tells this player.
 *
 * Before this, each screen kept its own copy of the invitation list, its own
 * poll and its own idea of what the player was currently doing — which is why
 * three different components were asking for invitations on three different
 * intervals, and why accepting one in a panel left it visible in the shell.
 *
 * Built on the app's existing `createStore`, not a state library: this is a
 * value plus a subscription, which is exactly what `useSyncExternalStore`
 * already wants.
 */

export interface PlayerState {
  /** Cards, by user id. Lists hold ids so a player updates in every list at once. */
  readonly playersById: Readonly<Record<string, SocialPlayer>>;
  readonly relationships: {
    readonly friends: { ids: readonly string[]; cursor: string | null; loaded: boolean };
    readonly following: { ids: readonly string[]; cursor: string | null; loaded: boolean };
    readonly followers: { ids: readonly string[]; cursor: string | null; loaded: boolean };
  };
  readonly sentInvitesById: Readonly<Record<string, InvitePresentation>>;
  readonly invitesById: Readonly<Record<string, InvitePresentation>>;
  readonly counts: { followers: number; following: number; friends: number };
  /** The player's single recoverable activity — was a 15s (and 750ms) poll. */
  readonly activity: Activity;
  /** Where a ready lobby wants us to go next, once the server says so. */
  readonly pendingRoomId: string | null;
  /**
   * Bumped whenever a lobby the player is in changes.
   *
   * Screens watch this instead of polling. The ready room used to re-fetch
   * twice a second because a seat change, a readiness tick or the start
   * countdown could arrive at any moment and it had no other way to hear about
   * them; now the matchmaker publishes and this counter tells the screen to
   * look once.
   */
  readonly lobbyTick: number;
  readonly unread: number;
  readonly cursor: number;
  readonly connection: ConnectionState;
  /**
   * Envelope ids already applied.
   *
   * Bounded, because it only has to cover the overlap between a live delivery
   * and a sync that re-reads the same rows — not the whole session.
   */
  readonly seen: readonly string[];
}

const SEEN_LIMIT = 200;

export const initialState: PlayerState = {
  playersById: {},
  relationships: {
    friends: { ids: [], cursor: null, loaded: false },
    following: { ids: [], cursor: null, loaded: false },
    followers: { ids: [], cursor: null, loaded: false },
  },
  invitesById: {},
  sentInvitesById: {},
  counts: { followers: 0, following: 0, friends: 0 },
  activity: { type: "idle" },
  pendingRoomId: null,
  lobbyTick: 0,
  unread: 0,
  cursor: 0,
  connection: "offline",
  seen: [],
};

export const playerStore: Store<PlayerState> = createStore(initialState);

/**
 * Pending, unexpired invitations, newest first. The only source any screen
 * should read — the shell's badge and the notifications list must never be able
 * to disagree about how many there are.
 *
 * Memoised, and it has to be: `useSyncExternalStore` compares snapshots with
 * `Object.is`, so a selector that derives a fresh array on every call reports a
 * change on every render and spins the component forever. React says so out
 * loud ("the result of getSnapshot should be cached"), but only once it has
 * already blown the update-depth limit.
 *
 * The cache key is the invitation map's identity *plus* a coarse time bucket.
 * Identity alone would be stable but would keep showing an invitation whose two
 * minutes ran out while nothing else happened; the bucket lets it drop out
 * within a few seconds without anything having to tick.
 */
const EXPIRY_BUCKET_MS = 5_000;
let cacheKey: { invites: PlayerState["invitesById"]; bucket: number } | null = null;
let cached: InvitePresentation[] = [];

export function pendingInvites(state: PlayerState, now = Date.now()): InvitePresentation[] {
  const bucket = Math.floor(now / EXPIRY_BUCKET_MS);
  if (cacheKey !== null && cacheKey.invites === state.invitesById && cacheKey.bucket === bucket) {
    return cached;
  }
  const next = Object.values(state.invitesById)
    .filter((invite) => invite.state === "pending" && invite.expiresAt > now)
    .sort((a, b) => b.createdAt - a.createdAt);
  // Keep the previous array when nothing actually changed, so a new time bucket
  // on its own does not look like new data to a subscriber.
  const unchanged = next.length === cached.length
    && next.every((invite, index) => cached[index]?.id === invite.id
      && cached[index]?.version === invite.version);
  cached = unchanged ? cached : next;
  cacheKey = { invites: state.invitesById, bucket };
  return cached;
}

function remember(seen: readonly string[], id: string): string[] {
  const next = [...seen, id];
  return next.length > SEEN_LIMIT ? next.slice(next.length - SEEN_LIMIT) : next;
}

/**
 * Fold one committed event into the state.
 *
 * Two guards, and they are the whole reason live delivery and `/channel/sync`
 * can share a code path: an envelope already applied by `id` is dropped, and an
 * entity is never moved backwards by an envelope carrying a version it has
 * already passed. A push that loses a race with a sync response is therefore
 * discarded rather than resurrecting an invitation the player already accepted.
 */
export function applyEnvelope(state: PlayerState, envelope: ChannelEnvelope): PlayerState {
  if (state.seen.includes(envelope.id)) return state;
  const seen = remember(state.seen, envelope.id);
  // Applying a live copy does not prove every lower sequence was seen. Only a
  // completed `/channel/sync` scan may advance the durable cursor.
  const base = { ...state, seen };

  switch (envelope.type) {
    case "invite.created":
    case "invite.resolved": {
      const invite = envelope.payload as InvitePresentation;
      const existing = base.sentInvitesById[invite.id] ?? base.invitesById[invite.id];
      if (existing !== undefined && existing.version >= invite.version) return base;
      if (base.sentInvitesById[invite.id]) return { ...base, sentInvitesById: { ...base.sentInvitesById, [invite.id]: invite } };
      return { ...base, invitesById: { ...base.invitesById, [invite.id]: invite } };
    }

    case "friend.followed":
    case "friend.unfollowed": {
      const { userId, friends, followers, friendsCount } = envelope.payload as {
        userId: string; friends: boolean; followers?: number; friendsCount?: number;
      };
      const card = base.playersById[userId];
      const followed = envelope.type === "friend.followed";
      return {
        ...base,
        counts: {
          ...base.counts,
          followers: typeof followers === "number"
            ? Math.max(0, followers)
            : Math.max(0, base.counts.followers + (followed ? 1 : -1)),
          // On follow, `friends` means the edge completed a mutual pair. On
          // unfollow it means the removed edge *was* mutual. A one-way follower
          // leaving therefore changes followers only, not the friend count.
          friends: typeof friendsCount === "number"
            ? Math.max(0, friendsCount)
            : Math.max(0, base.counts.friends + (friends ? (followed ? 1 : -1) : 0)),
        },
        // The list itself is re-read on next open; patching the card keeps an
        // already-rendered row honest without a refetch.
        ...(card === undefined ? {} : {
          playersById: {
            ...base.playersById,
            [userId]: { ...card, followsMe: followed, friends: followed && (card.following ?? false) },
          },
        }),
      };
    }

    case "presence.changed": {
      const { userId, online } = envelope.payload as { userId: string; online: boolean };
      const card = base.playersById[userId];
      if (card === undefined) return base;
      return {
        ...base,
        playersById: { ...base.playersById, [userId]: { ...card, presence: online ? "online" : "offline" } },
      };
    }

    case "activity.changed":
      return { ...base, activity: envelope.payload as Activity };

    case "queue.updated": {
      const payload = envelope.payload as { state: string; lobbyCode?: string | null; roomId?: string | null };
      // Keep this as a queue activity until Home follows `lobbyCode` into the
      // ready room. Replacing it with a hand-built lobby dropped the queue's
      // timer/config (and hard-coded Gold for ranked matches), while also
      // bypassing Home's automatic ready-room navigation.
      //
      // The current-activity guard is equally important on reconnect: sync can
      // replay an old `queue.updated` after `/me/activity` has already said the
      // player is idle. Such history must not resurrect an expired lobby.
      if (payload.state === "prepping" && payload.lobbyCode && base.activity.type === "queue") {
        return {
          ...base,
          activity: {
            ...base.activity,
            state: "prepping",
            lobbyCode: payload.lobbyCode,
          },
        };
      }
      return base;
    }

    case "room.ready": {
      const { roomId } = envelope.payload as { roomId: string };
      return { ...base, pendingRoomId: roomId, activity: { type: "match", roomId, away: false } };
    }

    case "lobby.updated":
      return { ...base, lobbyTick: base.lobbyTick + 1 };

    // Rewards and feed items are rendered from their own screens' data; the
    // event's job is only to tell those screens to look again.
    case "reward.granted":
    case "feed.item":
      return base;

    default:
      return base;
  }
}

export function applyEnvelopes(state: PlayerState, envelopes: readonly ChannelEnvelope[]): PlayerState {
  return envelopes.reduce(applyEnvelope, state);
}

/** Merge a page of cards, keeping ids in list order and cards shared across lists. */
export function mergeRelationshipPage(
  state: PlayerState,
  kind: "friends" | "following" | "followers",
  players: readonly SocialPlayer[],
  cursor: string | null,
  append: boolean,
): PlayerState {
  const playersById = { ...state.playersById };
  for (const player of players) playersById[player.userId] = player;
  const previous = append ? state.relationships[kind].ids : [];
  const ids = [...previous, ...players.map((player) => player.userId)];
  return {
    ...state,
    playersById,
    relationships: {
      ...state.relationships,
      [kind]: { ids: [...new Set(ids)], cursor, loaded: true },
    },
  };
}
